/** Net revenue, expenses and supplier deliveries. */
const finance = {
  en: {
    revenue: {
      title: "Net revenue",
      subtitle: "What you sold, what it cost you, and what you kept.",
      loadFailed: "Could not load your figures.",
      costWarning:
        "Only {percent} of what you sold has a buy price recorded, so your profit below is flattering. Add buy prices to your products to see the real number.",
      revenue: "Revenue",
      costOfGoods: "Cost of goods",
      grossProfit: "Gross profit",
      expenses: "Expenses",
      netRevenue: "Net revenue",
      ofRevenue: "{percent} of revenue",
      avgDaily: "Average per day",
      avgDailyHint: {
        one: "Net revenue over {count} day, closed days included.",
        other: "Net revenue over {count} days, closed days included.",
      },
      avgDailyNone: "This period has not started yet.",
      avgPerTradingDay: {
        one: "{amount} on a day you sold, from {count} day with sales",
        other: "{amount} on a day you sold, from {count} days with sales",
      },
      creditOwed:
        "Customers still owe you {amount}. Credit sales only count as revenue once they are paid, so this is not in the figures below.",
      whereExpenses: "Where the expenses went",
      sixMonths: "Last six months",
      dayByDay: "Day by day",
      nothingPeriod: "Nothing in this period yet.",
      columns: {
        day: "Day",
        sold: "Sold",
        cost: "Cost",
        spent: "Spent",
        kept: "Kept",
      },
      earnsMost: "What earns you the most",
      earnsMostHint:
        "Ordered by profit, not by margin — a big percentage on something nobody buys is not what pays the rent.",
      nothingSold: "Nothing sold in this period.",
      ownRecipe: "own recipe",
      unitsSold: "{count} sold · {amount}",
      uncosted: " · some without a cost",
    },
    expenses: {
      title: "Expenses",
      subtitle: "Rent, salaries, and everything else that comes out of your profit.",
      loadFailed: "Could not load your expenses.",
      saveFailed: "Could not record that expense.",
      removeFailed: "Could not remove that expense.",
      what: "What was it?",
      whatPlaceholder: "e.g. September rent",
      amount: "Amount ({currency})",
      category: "Category",
      when: "When",
      monthly: "This happens every month",
      photoAttached: "📎 Photo attached",
      attach: "📷 Attach receipt",
      record: "Record expense",
      totalPeriod: "Total for this period",
      nothingPeriod: "Nothing recorded for this period.",
      monthlyTag: "monthly",
      paidFrom: "Paid from",
      sources: { outside: "Somewhere else", safe: "My savings", drawer: "The till's drawer" },
      sourceHints: {
        outside: "Not tracked anywhere else. Only your expenses and net revenue change.",
        safe: "Also lowers your savings, once.",
        drawer: "Also comes out of the open drawer, once.",
      },
      sourceTags: { safe: "from savings", drawer: "from the drawer" },
      receipt: "receipt",
      categories: {
        rent: "Rent",
        salary: "Salaries",
        utilities: "Utilities",
        supplies: "Supplies",
        transport: "Transport",
        marketing: "Marketing",
        equipment: "Equipment",
        fees: "Fees",
        other: "Other",
      },
    },
    deliveries: {
      title: "Deliveries",
      subtitle:
        "What arrived from your suppliers. Recording one raises your stock and updates what each product costs you.",
      record: "Record a delivery",
      loadFailed: "Could not load deliveries.",
      recorded: "Delivery recorded — stock and buy prices are updated.",
      saveFailed: "Could not record that delivery.",
      supplier: "Supplier",
      invoice: "Invoice number",
      arrived: "Arrived",
      addProducts: "Add products",
      searchPlaceholder: "Start typing a product name…",
      inStock: "{count} in stock",
      columns: {
        product: "Product",
        quantity: "Quantity",
        lotTotal: "Lot total (optional)",
        destination: "Goes to",
      },
      destination: { shop: "The shop", warehouse: "The warehouse", split: "Split…" },
      toWarehouse: "To warehouse",
      allWarehouse: "· all to the warehouse",
      partWarehouse: "· {count} to the warehouse",
      removeLine: "Remove line",
      recording: "Recording…",
      submit: "Record delivery",
      incomplete: "Every line needs a quantity, and a split needs a whole number above 0 and below it.",
      recordedAwaiting: "Delivery recorded — stock is updated. Add the missing prices when you have them.",
      newProduct: "New product",
      newProductName: "Name",
      category: "Category",
      addLine: "Add",
      newProductHint: "It is added as a draft: counted in stock, but not sold until you give it a price.",
      newBadge: "new",
      later: "later",
      unpricedNote: { one: "{count} line has no price yet — stock is counted now, cost once you add it.", other: "{count} lines have no price yet — stock is counted now, cost once you add them." },
      paidNow: "Paid now ({currency})",
      paidNowHint: "Leave empty if nothing was paid — what is left stays owed to the supplier.",
      paidFrom: "Paid from",
      fromOutside: "Outside the till",
      fromDrawer: "The till's drawer",
      fromSafe: "My savings",
      awaiting: "Awaiting prices",
      addPrices: "Add prices",
      savePrices: "Save prices",
      pricesSaved: "Prices saved — buy prices are updated.",
      noPriceYet: "no price yet",
      none: "No deliveries recorded yet.",
      unnamed: "Supplier not named",
      each: "{amount} each",
    },
  },

  fr: {
    revenue: {
      title: "Revenu net",
      subtitle:
        "Ce que vous avez vendu, ce que cela vous a coûté et ce que vous avez gardé.",
      loadFailed: "Impossible de charger vos chiffres.",
      costWarning:
        "Seulement {percent} de vos ventes ont un prix d'achat enregistré : le bénéfice ci-dessous est donc flatteur. Ajoutez les prix d'achat à vos produits pour voir le vrai chiffre.",
      revenue: "Chiffre d'affaires",
      costOfGoods: "Coût des marchandises",
      grossProfit: "Marge brute",
      expenses: "Dépenses",
      netRevenue: "Revenu net",
      ofRevenue: "{percent} du chiffre d'affaires",
      avgDaily: "Moyenne par jour",
      avgDailyHint: {
        one: "Revenu net sur {count} jour, jours de fermeture compris.",
        other: "Revenu net sur {count} jours, jours de fermeture compris.",
      },
      avgDailyNone: "Cette période n'a pas encore commencé.",
      avgPerTradingDay: {
        one: "{amount} un jour de vente, sur {count} jour avec des ventes",
        other: "{amount} un jour de vente, sur {count} jours avec des ventes",
      },
      creditOwed:
        "Vos clients vous doivent encore {amount}. Une vente à crédit ne compte dans le chiffre d'affaires qu'une fois payée : ce montant n'est donc pas dans les chiffres ci-dessous.",
      whereExpenses: "Répartition des dépenses",
      sixMonths: "Six derniers mois",
      dayByDay: "Jour par jour",
      nothingPeriod: "Rien sur cette période pour l'instant.",
      columns: {
        day: "Jour",
        sold: "Vendu",
        cost: "Coût",
        spent: "Dépensé",
        kept: "Gardé",
      },
      earnsMost: "Ce qui vous rapporte le plus",
      earnsMostHint:
        "Classé par bénéfice, pas par marge — un gros pourcentage sur un produit que personne n'achète ne paie pas le loyer.",
      nothingSold: "Rien vendu sur cette période.",
      ownRecipe: "recette maison",
      unitsSold: "{count} vendus · {amount}",
      uncosted: " · certains sans coût",
    },
    expenses: {
      title: "Dépenses",
      subtitle: "Loyer, salaires et tout ce qui vient réduire votre bénéfice.",
      loadFailed: "Impossible de charger vos dépenses.",
      saveFailed: "Impossible d'enregistrer cette dépense.",
      removeFailed: "Impossible de supprimer cette dépense.",
      what: "De quoi s'agit-il ?",
      whatPlaceholder: "ex. loyer de septembre",
      amount: "Montant ({currency})",
      category: "Catégorie",
      when: "Date",
      monthly: "Revient chaque mois",
      photoAttached: "📎 Photo jointe",
      attach: "📷 Joindre le reçu",
      record: "Enregistrer la dépense",
      totalPeriod: "Total de la période",
      nothingPeriod: "Rien d'enregistré sur cette période.",
      monthlyTag: "mensuelle",
      paidFrom: "Payé avec",
      sources: { outside: "Autre source", safe: "Mon épargne", drawer: "Le tiroir-caisse" },
      sourceHints: {
        outside: "Rien d'autre n'est suivi. Seuls vos dépenses et le résultat net changent.",
        safe: "Baisse aussi votre épargne, une seule fois.",
        drawer: "Sort aussi du tiroir ouvert, une seule fois.",
      },
      sourceTags: { safe: "depuis l'épargne", drawer: "depuis le tiroir" },
      receipt: "reçu",
      categories: {
        rent: "Loyer",
        salary: "Salaires",
        utilities: "Charges",
        supplies: "Fournitures",
        transport: "Transport",
        marketing: "Marketing",
        equipment: "Équipement",
        fees: "Frais",
        other: "Autre",
      },
    },
    deliveries: {
      title: "Livraisons",
      subtitle:
        "Ce qui est arrivé de vos fournisseurs. En enregistrer une augmente votre stock et met à jour le coût de chaque produit.",
      record: "Enregistrer une livraison",
      loadFailed: "Impossible de charger les livraisons.",
      recorded: "Livraison enregistrée — stock et prix d'achat mis à jour.",
      saveFailed: "Impossible d'enregistrer cette livraison.",
      supplier: "Fournisseur",
      invoice: "N° de facture",
      arrived: "Reçue le",
      addProducts: "Ajouter des produits",
      searchPlaceholder: "Tapez le nom d'un produit…",
      inStock: "{count} en stock",
      columns: {
        product: "Produit",
        quantity: "Quantité",
        lotTotal: "Total du lot (facultatif)",
        destination: "Destination",
      },
      destination: { shop: "La boutique", warehouse: "L'entrepôt", split: "Répartir…" },
      toWarehouse: "À l'entrepôt",
      allWarehouse: "· tout à l'entrepôt",
      partWarehouse: "· {count} à l'entrepôt",
      removeLine: "Retirer la ligne",
      recording: "Enregistrement…",
      submit: "Enregistrer la livraison",
      incomplete: "Chaque ligne doit avoir une quantité, et une répartition un entier au-dessus de 0 et en dessous d'elle.",
      recordedAwaiting: "Livraison enregistrée — le stock est à jour. Ajoutez les prix manquants quand vous les aurez.",
      newProduct: "Nouveau produit",
      newProductName: "Nom",
      category: "Catégorie",
      addLine: "Ajouter",
      newProductHint: "Il est ajouté en brouillon : compté en stock, mais pas vendu tant qu'il n'a pas de prix.",
      newBadge: "nouveau",
      later: "plus tard",
      unpricedNote: { one: "{count} ligne n'a pas encore de prix — le stock est compté, le coût quand vous l'ajouterez.", other: "{count} lignes n'ont pas encore de prix — le stock est compté, le coût quand vous les ajouterez." },
      paidNow: "Payé maintenant ({currency})",
      paidNowHint: "Laissez vide si rien n'a été payé — le reste reste dû au fournisseur.",
      paidFrom: "Payé depuis",
      fromOutside: "Hors caisse",
      fromDrawer: "Le tiroir de la caisse",
      fromSafe: "Mon épargne",
      awaiting: "Prix à compléter",
      addPrices: "Ajouter les prix",
      savePrices: "Enregistrer les prix",
      pricesSaved: "Prix enregistrés — les prix d'achat sont à jour.",
      noPriceYet: "pas encore de prix",
      none: "Aucune livraison enregistrée pour l'instant.",
      unnamed: "Fournisseur non renseigné",
      each: "{amount} l'unité",
    },
  },

  ar: {
    revenue: {
      title: "صافي الإيرادات",
      subtitle: "ما بعته، وكم كلّفك، وما بقي لك.",
      loadFailed: "تعذّر تحميل أرقامك.",
      costWarning:
        "فقط {percent} مما بعته له سعر شراء مسجّل، لذا يبدو ربحك أدناه أفضل من حقيقته. أضف أسعار الشراء لمنتجاتك لترى الرقم الحقيقي.",
      revenue: "الإيرادات",
      costOfGoods: "تكلفة البضاعة",
      grossProfit: "الربح الإجمالي",
      expenses: "المصاريف",
      netRevenue: "صافي الإيرادات",
      ofRevenue: "{percent} من الإيرادات",
      avgDaily: "المتوسط اليومي",
      avgDailyHint: {
        zero: "صافي الإيرادات، أيام الإغلاق محسوبة.",
        one: "صافي الإيرادات على يوم واحد، أيام الإغلاق محسوبة.",
        two: "صافي الإيرادات على يومين، أيام الإغلاق محسوبة.",
        few: "صافي الإيرادات على {count} أيام، أيام الإغلاق محسوبة.",
        many: "صافي الإيرادات على {count} يوماً، أيام الإغلاق محسوبة.",
        other: "صافي الإيرادات على {count} يوم، أيام الإغلاق محسوبة.",
      },
      avgDailyNone: "لم تبدأ هذه الفترة بعد.",
      avgPerTradingDay: {
        zero: "{amount} في يوم البيع",
        one: "{amount} في يوم بيع، من يوم واحد فيه مبيعات",
        two: "{amount} في يوم بيع، من يومين فيهما مبيعات",
        few: "{amount} في يوم بيع، من {count} أيام فيها مبيعات",
        many: "{amount} في يوم بيع، من {count} يوماً فيها مبيعات",
        other: "{amount} في يوم بيع، من {count} يوم فيها مبيعات",
      },
      creditOwed:
        "لا يزال العملاء مدينين لك بمبلغ {amount}. لا تُحتسب المبيعات بالدين ضمن الإيرادات إلا بعد سدادها، لذا هذا المبلغ غير داخل في الأرقام أدناه.",
      whereExpenses: "أين ذهبت المصاريف",
      sixMonths: "آخر ستة أشهر",
      dayByDay: "يوماً بيوم",
      nothingPeriod: "لا شيء في هذه الفترة بعد.",
      columns: {
        day: "اليوم",
        sold: "المبيعات",
        cost: "التكلفة",
        spent: "المصاريف",
        kept: "الصافي",
      },
      earnsMost: "ما يحقق لك أكبر ربح",
      earnsMostHint:
        "مرتّب حسب الربح لا حسب الهامش — نسبة كبيرة على منتج لا يشتريه أحد لا تدفع الإيجار.",
      nothingSold: "لم يُبع شيء في هذه الفترة.",
      ownRecipe: "وصفة خاصة",
      unitsSold: "بيع {count} · {amount}",
      uncosted: " · بعضها بلا تكلفة",
    },
    expenses: {
      title: "المصاريف",
      subtitle: "الإيجار والرواتب وكل ما يُقتطع من ربحك.",
      loadFailed: "تعذّر تحميل مصاريفك.",
      saveFailed: "تعذّر تسجيل هذا المصروف.",
      removeFailed: "تعذّر حذف هذا المصروف.",
      what: "ما هو؟",
      whatPlaceholder: "مثال: إيجار سبتمبر",
      amount: "المبلغ ({currency})",
      category: "الفئة",
      when: "التاريخ",
      monthly: "يتكرر كل شهر",
      photoAttached: "📎 الصورة مرفقة",
      attach: "📷 إرفاق الإيصال",
      record: "تسجيل المصروف",
      totalPeriod: "المجموع لهذه الفترة",
      nothingPeriod: "لم يُسجَّل شيء في هذه الفترة.",
      monthlyTag: "شهري",
      paidFrom: "مدفوع من",
      sources: { outside: "مصدر آخر", safe: "مدخراتي", drawer: "درج الصندوق" },
      sourceHints: {
        outside: "لا يُتتبَّع في مكان آخر. يتغير فقط سجل المصاريف وصافي الإيراد.",
        safe: "ينقص من مدخراتك أيضاً، مرة واحدة.",
        drawer: "يخرج أيضاً من الدرج المفتوح، مرة واحدة.",
      },
      sourceTags: { safe: "من المدخرات", drawer: "من الدرج" },
      receipt: "الإيصال",
      categories: {
        rent: "إيجار",
        salary: "رواتب",
        utilities: "فواتير الخدمات",
        supplies: "لوازم",
        transport: "نقل",
        marketing: "تسويق",
        equipment: "معدات",
        fees: "رسوم",
        other: "أخرى",
      },
    },
    deliveries: {
      title: "التوريدات",
      subtitle: "ما وصل من مورّديك. تسجيل توريد يرفع مخزونك ويحدّث تكلفة كل منتج.",
      record: "تسجيل توريد",
      loadFailed: "تعذّر تحميل التوريدات.",
      recorded: "تم تسجيل التوريد — تم تحديث المخزون وأسعار الشراء.",
      saveFailed: "تعذّر تسجيل هذا التوريد.",
      supplier: "المورّد",
      invoice: "رقم الفاتورة",
      arrived: "تاريخ الوصول",
      addProducts: "إضافة منتجات",
      searchPlaceholder: "ابدأ بكتابة اسم منتج…",
      inStock: "{count} في المخزون",
      columns: {
        product: "المنتج",
        quantity: "الكمية",
        lotTotal: "مجموع الدفعة (اختياري)",
        destination: "تذهب إلى",
      },
      destination: { shop: "المتجر", warehouse: "المخزن", split: "تقسيم…" },
      toWarehouse: "إلى المخزن",
      allWarehouse: "· كلها إلى المخزن",
      partWarehouse: "· {count} إلى المخزن",
      removeLine: "إزالة السطر",
      recording: "جارٍ التسجيل…",
      submit: "تسجيل التوريد",
      incomplete: "كل سطر يحتاج إلى كمية، والتقسيم يحتاج عدداً صحيحاً أكبر من 0 وأقل منها.",
      recordedAwaiting: "تم تسجيل التوريد — المخزون محدّث. أضف الأسعار الناقصة عندما تتوفر لديك.",
      newProduct: "منتج جديد",
      newProductName: "الاسم",
      category: "الفئة",
      addLine: "إضافة",
      newProductHint: "يُضاف كمسودة: يُحتسب في المخزون، لكن لا يُباع حتى تحدد سعره.",
      newBadge: "جديد",
      later: "لاحقاً",
      unpricedNote: { zero: "كل الأسطر مسعّرة.", one: "سطر واحد بلا سعر بعد — المخزون محتسب الآن، والتكلفة عند إضافته.", two: "سطران بلا سعر بعد — المخزون محتسب الآن، والتكلفة عند إضافتهما.", few: "{count} أسطر بلا سعر بعد — المخزون محتسب الآن، والتكلفة عند إضافتها.", many: "{count} سطراً بلا سعر بعد — المخزون محتسب الآن، والتكلفة عند إضافتها.", other: "{count} سطر بلا سعر بعد — المخزون محتسب الآن، والتكلفة عند إضافتها." },
      paidNow: "المدفوع الآن ({currency})",
      paidNowHint: "اتركه فارغاً إن لم يُدفع شيء — ما تبقى يبقى ديناً للمورد.",
      paidFrom: "مدفوع من",
      fromOutside: "خارج الصندوق",
      fromDrawer: "درج الصندوق",
      fromSafe: "مدخراتي",
      awaiting: "بانتظار الأسعار",
      addPrices: "إضافة الأسعار",
      savePrices: "حفظ الأسعار",
      pricesSaved: "تم حفظ الأسعار — أسعار الشراء محدّثة.",
      noPriceYet: "بلا سعر بعد",
      none: "لم يُسجَّل أي توريد بعد.",
      unnamed: "مورّد غير مسمّى",
      each: "{amount} للوحدة",
    },
  },
};

export default finance;
